/**
 * Schémas Vague B — Système de références.
 *
 * Le candidat invite un référent (prof, ancien manager, maître de stage…).
 * Le référent reçoit un mail avec un lien magique et peut donner son avis
 * via un formulaire de notation (8 critères × 1-5 étoiles + texte libre +
 * recommandation globale).
 */
import { z } from 'zod';
import { emailSchema } from './common.js';
// ─── Types de relation ───────────────────────────────────────────────────
export const REFERENCE_RELATION_TYPES = [
    'enseignant',
    'ancien_enseignant',
    'manager_actuel',
    'manager_ancien',
    'collegue_actuel',
    'collegue_ancien',
    'maitre_stage',
    'maitre_apprentissage',
    'autre',
];
export const REFERENCE_RELATION_LABELS = {
    enseignant: 'Enseignant·e',
    ancien_enseignant: 'Ancien·ne enseignant·e',
    manager_actuel: 'Manager actuel',
    manager_ancien: 'Ancien manager',
    collegue_actuel: 'Collègue actuel·le',
    collegue_ancien: 'Ancien·ne collègue',
    maitre_stage: 'Maître de stage',
    maitre_apprentissage: "Maître d'apprentissage",
    autre: 'Autre',
};
// ─── Statuts ─────────────────────────────────────────────────────────────
export const REFERENCE_STATUSES = [
    'pending',
    'declined',
    'completed',
    'expired',
    'revoked',
];
export const REFERENCE_STATUS_LABELS = {
    pending: 'En attente de réponse',
    declined: 'Déclinée',
    completed: 'Avis donné',
    expired: 'Expirée',
    revoked: 'Annulée',
};
// ─── Recommandation globale ──────────────────────────────────────────────
export const REFERENCE_RECOMMENDATIONS = ['yes', 'no', 'with_reservations'];
export const REFERENCE_RECOMMENDATION_LABELS = {
    yes: 'Oui, sans hésiter',
    with_reservations: 'Oui, avec quelques réserves',
    no: 'Non',
};
// ─── Critères de notation HCR ─────────────────────────────────────────────
// Adaptés au secteur hôtellerie / restauration. Le référent note chaque
// critère sur 5 étoiles. La grille est figée pour permettre l'agrégation
// statistique côté employeur (top 3 critères / radar / etc.).
export const REFERENCE_CRITERIA = [
    {
        code: 'assiduite',
        label: 'Assiduité & ponctualité',
        description: 'Présence régulière, fiabilité aux horaires.',
    },
    {
        code: 'rigueur',
        label: 'Rigueur & propreté',
        description: 'Hygiène HACCP, organisation du poste de travail.',
    },
    {
        code: 'sens_service',
        label: 'Sens du service',
        description: 'Posture client, écoute, courtoisie.',
    },
    {
        code: 'esprit_equipe',
        label: "Esprit d'équipe",
        description: 'Collaboration en brigade, sous pression.',
    },
    {
        code: 'adaptabilite',
        label: 'Adaptabilité',
        description: 'Coup de feu, imprévus, flexibilité.',
    },
    {
        code: 'apprentissage',
        label: 'Capacité à apprendre',
        description: 'Montée en compétence, prise de feedback.',
    },
    {
        code: 'autonomie',
        label: 'Autonomie',
        description: 'Initiative, prise de décision.',
    },
    {
        code: 'savoir_etre',
        label: 'Savoir-être global',
        description: 'Attitude générale, professionnalisme.',
    },
];
export const referenceCriterionCodes = REFERENCE_CRITERIA.map((c) => c.code);
// Schéma des notes : tous les critères doivent être renseignés (1-5).
export const referenceRatingsSchema = z.object(Object.fromEntries(REFERENCE_CRITERIA.map((c) => [
    c.code,
    z.number().int().min(1).max(5),
])));
// ─── Création par le candidat ────────────────────────────────────────────
export const referenceCreateSchema = z.object({
    refereeFirstName: z.string().trim().min(1).max(100),
    refereeLastName: z.string().trim().min(1).max(100),
    refereeEmail: emailSchema,
    refereeCompany: z.string().trim().max(200).optional().or(z.literal('')),
    refereeRole: z.string().trim().max(150).optional().or(z.literal('')),
    relationType: z.enum(REFERENCE_RELATION_TYPES),
});
// ─── Soumission par le référent (page magique) ──────────────────────────
export const referenceSubmitSchema = z.object({
    ratings: referenceRatingsSchema,
    freeOpinion: z.string().trim().max(2000).optional().or(z.literal('')),
    recommendation: z.enum(REFERENCE_RECOMMENDATIONS),
    publicOpinion: z.boolean().default(false),
});
//# sourceMappingURL=reference.js.map